import { AtSign, Eye, EyeOff, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button.jsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card.jsx';
import { Input } from "../components/ui/Input.jsx";
import { useToast } from "../hooks/useToast.js";
import { getCurrentUser, login } from "../services/authService.js";
import { getCompany } from "../services/companyService.js";
import { useFormValidation } from "../hooks/useFormValidation.js";

// Custom validation for this form
const loginValidation = {
  email: {
    required: "Email is required",
    pattern: {
      regex: /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: "Invalid email format",
    },
  },
  password: {
    required: "Password is required",
    minLength: { value: 6, message: "Password must be at least 6 characters" },
  },
};

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use validation hook
  const {
    form,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
  } = useFormValidation(
    {
      email: "",
      password: "",
    },
    loginValidation
  );

  const { email, password } = form;

  useEffect(() => {
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateAll()) {
      toast({
        title: "Validation Error",
        description: "Check the credentials.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const authResponse = await login({ email, password });

      if (authResponse?.success) {
        const currentUser = await getCurrentUser();

        if (currentUser?.success && currentUser.user.user_role === "business_admin") {
          try {
            const currentCompany = await getCompany();

            if(currentCompany){
            toast({
              title: "Welcome back!",
              description: "Successfully logged in to your account.",
            });

            navigate("/dashboard");
            } else {
              toast({
                title: "oops!",
                description: "Need to create a company to get started.",
              });

              navigate("/add-company");
            }
          } catch (err) {
            toast({
              title: "Error",
              description: err.message || "Something went wrong",
              variant: "destructive",
            });
          }
        } else if (currentUser?.success && currentUser.user.user_role === "sales_person") {
            toast({
              title: "Welcome back!",
              description: "Successfully logged in to your account.",
            });
            navigate("/team");
        } else {
          toast({
            title: "Login failed",
            description: currentUser?.error || "Unable to fetch user session",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Login failed",
          description: authResponse?.error || "Invalid credentials",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[100dvh] p-4 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h3 className="mb-2 text-3xl font-bold text-foreground">Customer Relationship Manager</h3>
          <p>by Nipralo Technologies</p>
        </div>

        <Card className="border-none shadow-lg">
          <CardHeader className="pb-4 text-center">
            <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
            <CardDescription>Log in to manage your sales activities.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <AtSign className="absolute w-5 h-5 left-3 top-3 text-muted-foreground" />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`pl-10 h-12 bg-muted border-none ${errors.email ? "!border !border-red-500" : ""}`}
                    required
                  />
                </div>
                {touched.email && errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <Lock className="absolute w-5 h-5 left-3 top-3 text-muted-foreground" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`pl-10 pr-10 h-12 bg-muted border-none ${errors.password ? "!border !border-red-500" : ""}`}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-muted-foreground"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              <div className="text-right">
                <button type="button" className="text-sm text-primary hover:underline" onClick={() => navigate("/forgot-password")}>
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                className=" w-full h-10 text-sm rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
                disabled={isLoading}
              >
                {isLoading ? "Logging in..." : "Log In"}
              </Button>
            </form>

            <div className="text-sm text-center text-muted-foreground">
              Don't have an account?{" "}
              <button
                className="text-primary hover:underline"
                onClick={() => navigate("/send-otp")}
              >
                Sign up
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
